import React, { useState } from 'react';
import { 
  Layout, 
  Form, 
  Input, 
  InputNumber,
  Button, 
  Alert, 
  Typography,
  Row,
  Col,
  Grid,
  Space,
  message,
  Card
} from 'antd';
import { 
  UserOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useClientAdmin } from '../../context/ClientAdminContext';
import { API_ORIGIN } from '../../config';

const { Content, Header } = Layout;
const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const DanceOn = () => {
  const navigate = useNavigate();
  const { clientAdmin } = useClientAdmin();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [interestWordCount, setInterestWordCount] = useState(0);
  const [backgroundWordCount, setBackgroundWordCount] = useState(0);

  const screens = Grid.useBreakpoint();
  const isSmall = !screens.md;

  const formFontColour = '#FFAC00';
  const maxWords = 200;

  const formId = 8; // DanceOn form ID
  
  //Get specific activity status
  const [isFormActive, setIsFormActive] = useState(true); // Default to true to show form initially

  // Fetch form activity status on component mount
  React.useEffect(() => {
    const fetchFormStatus = async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/api/clientadmin/specificForm?id=${formId}`, {
          method: 'GET',
        });
        const data = await response.json();
        
        if (response.ok && data.success) {
          // Backend returns 0 or 1 (tinyint) - convert to boolean
          const isActive = data.activeForm === 1 || data.isActive === 1;
          setIsFormActive(isActive);
        } else {
          setIsFormActive(false); // Disable form if fetch fails
        }
      } catch (error) {
        setIsFormActive(false); // Disable form on error
      }
    };

    fetchFormStatus();
  }, []);

  // Handle word count for interest field (max 200 words)
  const handleInterestChange = (e) => {
    const text = (e && e.target && e.target.value) ? e.target.value : '';
    const words = text.match(/\b\w+\b/g) || [];
    const currentWordCount = words.length;

    if (currentWordCount > maxWords) {
      const trimmedText = words.slice(0, maxWords).join(' ');
      form.setFieldsValue({ interest: trimmedText });
      setInterestWordCount(maxWords);
      message.warning(`Interest is limited to ${maxWords} words`);
    } else {
      // Ensure the Form knows about the current textarea value so validation works
      form.setFieldsValue({ interest: text });
      setInterestWordCount(currentWordCount);
    }
  };

  // Handle word count for background field (max 200 words)
  const handleBackgroundChange = (e) => {
    const text = (e && e.target && e.target.value) ? e.target.value : '';
    const words = text.match(/\b\w+\b/g) || [];
    const currentWordCount = words.length;

    if (currentWordCount > maxWords) {
      const trimmedText = words.slice(0, maxWords).join(' ');
      form.setFieldsValue({ background: trimmedText });
      setBackgroundWordCount(maxWords);
      message.warning(`Background is limited to ${maxWords} words`);
    } else {
      // Ensure the Form knows about the current textarea value so validation works
      form.setFieldsValue({ background: text });
      setBackgroundWordCount(currentWordCount);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Build JSON payload (no file upload expected)
      const payload = {
        name: values.name,
        surname: values.surname,
        age: values.age,
        interest: values.interest,
        background: values.background || '',
        email: values.email,
        contact: values.contact,
      };

      // Debug: log payload

      const response = await fetch(`${API_ORIGIN}/api/danceon/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
      });

      // Try to parse JSON, but handle non-JSON responses (e.g. HTML 500 page)
      let data;
      try {
        data = await response.json();
      } catch (err) {
        const text = await response.text();
        data = { success: false, error: text };
      }

      if (response.ok && data.success) {
        setSuccess(true);
        message.success('Application submitted successfully!');
        form.resetFields();
        window.scrollTo(0, 0);
      } else {
        const errMsg = data.error || `Submission failed (status ${response.status})`;
        setError(errMsg);
        message.error(errMsg);
      }
    } catch (error) {
      setError('An error occurred while submitting the application. Please try again.');
      message.error('An error occurred while submitting the application');
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <style>{`
        /* Smooth transitions for buttons */
        .ant-btn {
          transition: transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease, border-color 180ms ease, color 180ms ease;
        }

        /* Hover colour for all non-primary buttons */
        .ant-btn:not(.danceon-primary):hover {
          background-color: ${formFontColour} !important;
          border-color: ${formFontColour} !important;
          color: #fff !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 20px rgba(255,172,0,0.12) !important;
        }

        /* Primary submit button base style */
        .danceon-primary {
          background-color: ${formFontColour} !important;
          border-color: ${formFontColour} !important;
          color: #fff !important;
          transform: translateY(0);
          box-shadow: 0 6px 18px rgba(24,144,255,0.08);
        }

        /* Primary submit hover animation */
        .danceon-primary:hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 14px 36px rgba(255,172,0,0.22) !important;
        }

        .danceon-primary:active {
          transform: translateY(-1px) !important;
        }
      `}</style>
      <Layout style={{ minHeight: '100vh', background: '#fafafa', overflowX: 'hidden' }}>
      <Header style={{ 
        padding: '12px 16px', 
        background: '#fff', 
        borderBottom: '1px solid #f0f0f0',
        height: 'auto',
        overflowX: 'hidden'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ 
            flex: '0 0 auto',
            display: 'flex', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingRight: isSmall ? 8 : 0
          }}>
            <img
              src="/images/FM_Logo.png"
              alt="Festivals Malta"
              onClick={() => navigate('/')}
              style={{
                height: isSmall ? '32px' : '36px',
                width: 'auto',
                maxWidth: '160px',
                cursor: 'pointer',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      </Header>

      <Content style={{ padding: 'clamp(20px, 5vw, 48px)', overflowX: 'hidden' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: isSmall ? '0 12px' : '0 20px', width: '100%', boxSizing: 'border-box' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(24px, 6vw, 48px)' }}>
             <img
              src="/images/DFM_Logo.png"
              alt="Dance Festival Malta"
              style={{
                height: isSmall ? '100px' : 'clamp(140px, 22vw, 220px)',
                maxWidth: '100%',
                maxHeight: '240px',
                objectFit: 'contain'
              }}
            />
            {!isFormActive && (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <Title level={3} style={{ color: '#222222', marginBottom: '16px' }}>
                  The application form is currently closed.
                </Title>
              </div>
            )}
            <Title level={1} style={{ color: '#222', letterSpacing: '1px', marginBottom: '16px', fontSize: 'clamp(24px, 6vw, 36px)', marginTop: '24px' }}>
              Public Call for Mature Dancers
            </Title>
            <Title level={2} style={{ color: formFontColour, marginBottom: '8px', fontSize: 'clamp(20px, 5vw, 28px)' }}>
              DANCE ON! — ECHOES OF A LIFE IN MOTION
            </Title>
            <Paragraph style={{ color: '#555', fontSize: 'clamp(14px, 4vw, 18px)', marginBottom: '24px' }}>
              Dance Festival Malta 2026
            </Paragraph>
            
            {/* Dancers Image */}
            <div style={{ marginBottom: '24px' }}>
              <img
                src="/images/Dancers.jpg"
                alt="Dancers"
                style={{
                  width: '100%',
                  maxWidth: '800px',
                  height: 'auto',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
              <Paragraph style={{ color: '#888', fontSize: '12px', fontStyle: 'italic', marginTop: '8px' }}>
                Photo by Elisa von Brockdorff
              </Paragraph>
            </div>
          </div>

          {success && (
            <Alert
              message="Application submitted successfully!"
              description="A representative will get back to you once it is vetted."
              type="success"
              style={{ marginBottom: '24px' }}
              closable
              onClose={() => setSuccess(false)}
            />
          )}

          {error && (
            <Alert
              message={error}
              type="error"
              style={{ marginBottom: '24px' }}
              closable
              onClose={() => setError('')}
            />
          )}

          <Card 
            style={{ 
              borderRadius: '12px', 
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: '1px solid #f0f0f0',
              maxWidth: '100%',
              overflowX: 'hidden'
            }}
            styles={{ body: { padding: isSmall ? 20 : 40 } }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              requiredMark={false}
              style={{ maxWidth: '100%', overflowX: 'hidden' }}
              disabled={!isFormActive}
            >
              <Row gutter={isSmall ? 12 : 24} style={{ maxWidth: '100%' }}>

              {/* Personal Information Section */}
              <Col xs={24}>
                <Title level={4} style={{ color: formFontColour, marginBottom: 16 }}>
                  <UserOutlined /> Personal Information
                </Title>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Name</span>}
                  name="name"
                  rules={[{ required: true, message: 'Please enter your name' }]}
                >
                  <Input placeholder="Enter your name" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Surname</span>}
                  name="surname"
                  rules={[{ required: true, message: 'Please enter your surname' }]}
                >
                  <Input placeholder="Enter your surname" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Age</span>}
                  name="age"
                  rules={[
                    { required: true, message: 'Please enter your age' },
                    { type: 'number', min: 1, max: 120, message: 'Please enter a valid age' }
                  ]}
                >
                  <InputNumber placeholder="Enter your age" style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Contact Number</span>}
                  name="contact"
                  rules={[{ required: true, message: 'Please enter your contact number' }]}
                >
                  <Input placeholder="Enter your contact number" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Email</span>}
                  name="email"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <Input placeholder="Enter your email address" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Interest</span>}
                  name="interest"
                  rules={[{ required: true, message: 'Please describe your interest' }]}
                >
                    <div>
                      <TextArea 
                        rows={4} 
                        placeholder="Describe your interest in DanceOn 2026"
                        onChange={handleInterestChange}
                      />
                      <div style={{ 
                        textAlign: 'right', 
                        marginTop: '4px', 
                        fontSize: '12px',
                        color: interestWordCount > maxWords ? '#ff4d4f' : '#888'
                      }}>
                        {interestWordCount}/{maxWords} words
                      </div>
                    </div>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Dance background and experience (if any)</span>}
                  name="background"
                >
                  <div>
                    <TextArea 
                      rows={4} 
                      placeholder="Describe your dance background and experience (if any)"
                      onChange={handleBackgroundChange}
                    />
                    <div style={{ 
                      textAlign: 'right', 
                      marginTop: '4px', 
                      fontSize: '12px',
                      color: backgroundWordCount > maxWords ? '#ff4d4f' : '#888'
                    }}>
                      {backgroundWordCount}/{maxWords} words
                    </div>
                  </div>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item>
                  <Button 
                    className="danceon-primary"
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    size="large"
                    block
                    style={{
                      width: '100%', 
                      height: '56px',
                      fontSize: '16px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(24, 144, 255, 0.12)'
                    }}
                  >
                    Submit Application
                  </Button>
                </Form.Item>
              </Col>
              </Row>
            </Form>
          </Card>
        </div>
      </Content>
    </Layout>
    </>
  );
};

export default DanceOn;
